/**
 * The Workflow Builder's trigger ("Start the workflow…", Slack): a link in a channel, a new member, an emoji reaction,
 * a schedule (day, time, zone, repeat, weekly days) or a webhook with its variables.
 */
import type { ScheduleRepeat, WebhookVariable, WorkflowTrigger, WorkflowTriggerType } from "@bundle/contracts";
import { WorkflowBuilder } from "@bundle/core";
import { useMemo, useState } from "react";
import { Button, DatePicker, IconButton, Popover, Pressable, Select, Stack, Text, TextField } from "../../primitives/index.ts";
import { useFormat, useLocale, useT, useTr } from "../../i18n/I18nProvider.tsx";
import { useWorkspace } from "../../hooks/index.ts";

const REPEATS: readonly ScheduleRepeat[] = ["none", "daily", "weekdays", "weekly", "monthly"];
const WEBHOOK_TYPES: readonly WebhookVariable["type"][] = ["text", "user", "channel"];

/** Every IANA zone the browser knows (the schedule's zone menu), with the current one first if it's missing. */
function zones(current: string): string[] {
  const all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  return all.includes(current) ? all : [current, ...all];
}

export function TriggerEditor({ trigger, onType, onChange, testID }: { trigger: WorkflowTrigger; onType: (t: WorkflowTriggerType) => void; onChange: (t: WorkflowTrigger) => void; testID: string }) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const cache = ws.state.cache;
  const channels = useMemo(() => (cache === undefined ? [] : WorkflowBuilder.workflowChannels(cache)), [cache]);
  return (
    <Stack gap="3" testID={testID}>
      <Select
        label={t("workflow.trigger.label")}
        value={trigger.type}
        onChange={onType}
        options={WorkflowBuilder.TRIGGER_TYPES.map((type) => ({ value: type, label: tr(WorkflowBuilder.triggerLabel(type)) }))}
        testID={`${testID}-type`}
      />
      {"channelId" in trigger ? (
        <Select
          label={t("workflow.trigger.channel")}
          value={trigger.channelId === "" ? undefined : trigger.channelId}
          placeholder={t("workflow.channel.choose")}
          onChange={(channelId) => onChange({ ...trigger, channelId })}
          options={channels.map((c) => ({ value: c.id, label: c.name, icon: c.private ? ("lock" as const) : ("hash" as const) }))}
          testID={`${testID}-channel`}
        />
      ) : null}
      {trigger.type === "reaction" ? (
        <TextField label={t("workflow.trigger.emoji")} value={trigger.emoji} onChange={(emoji) => onChange({ ...trigger, emoji })} helperText={t("workflow.trigger.emoji.help")} testID={`${testID}-emoji`} />
      ) : null}
      {trigger.type === "schedule" ? <ScheduleEditor trigger={trigger} onChange={onChange} testID={`${testID}-schedule`} /> : null}
      {trigger.type === "webhook" ? <WebhookVariables trigger={trigger} onChange={onChange} testID={`${testID}-webhook`} /> : null}
      <Text variant="caption" color="text.tertiary">
        {tr(WorkflowBuilder.triggerHelp(trigger.type))}
      </Text>
    </Stack>
  );
}

function ScheduleEditor({ trigger, onChange, testID }: { trigger: Extract<WorkflowTrigger, { type: "schedule" }>; onChange: (t: WorkflowTrigger) => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const locale = useLocale();
  const [picking, setPicking] = useState(false);
  const s = trigger.schedule;
  const { date, time } = WorkflowBuilder.scheduleParts(s);
  const set = (schedule: typeof s) => onChange({ ...trigger, schedule });
  const slots = WorkflowBuilder.timeSlots();
  const times = slots.includes(time) ? slots : [time, ...slots];
  const [y, m, d] = date.split("-").map(Number);
  const days = s.days ?? [new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay()];
  return (
    <Stack gap="3" testID={testID}>
      <Stack direction="horizontal" gap="2" align="end" wrap>
        <Stack gap="1">
          <Text variant="footnote" weight="medium" color="text.secondary">
            {t("workflow.schedule.date")}
          </Text>
          <Popover
            open={picking}
            onClose={() => setPicking(false)}
            side="bottom"
            align="start"
            anchor={<Button label={fmt.date({ year: y!, month: m!, day: d! })} icon="calendar" onPress={() => setPicking(!picking)} testID={`${testID}-date`} />}
          >
            <DatePicker
              value={date}
              locale={locale}
              onChange={(day) => {
                set(WorkflowBuilder.withScheduleParts(s, day, time));
                setPicking(false);
              }}
              testID={`${testID}-calendar`}
            />
          </Popover>
        </Stack>
        <Stack grow>
          <Select label={t("workflow.schedule.time")} value={time} onChange={(v) => set(WorkflowBuilder.withScheduleParts(s, date, v))} options={times.map((v) => ({ value: v, label: v }))} testID={`${testID}-time`} />
        </Stack>
      </Stack>
      <Select label={t("workflow.schedule.zone")} value={s.timeZone} onChange={(zone) => set(WorkflowBuilder.withTimeZone(s, zone))} options={zones(s.timeZone).map((z) => ({ value: z, label: z.replaceAll("_", " ") }))} testID={`${testID}-zone`} />
      <Select
        label={t("workflow.schedule.repeat")}
        value={s.repeat}
        onChange={(repeat) => {
          const { days: _d, ...rest } = s;
          set(repeat === "weekly" ? { ...rest, repeat, days } : { ...rest, repeat });
        }}
        options={REPEATS.map((r) => ({ value: r, label: t(`workflow.schedule.repeat.${r}`) }))}
        testID={`${testID}-repeat`}
      />
      {s.repeat === "weekly" ? (
        <Stack direction="horizontal" gap="1" wrap accessibilityLabel={t("workflow.schedule.days")} testID={`${testID}-days`}>
          {[0, 1, 2, 3, 4, 5, 6].map((day) => {
            const on = days.includes(day);
            return (
              <Pressable key={day} radius="full" selected={on} onPress={() => set({ ...s, days: WorkflowBuilder.toggleWeekday(days, day) })} accessibilityLabel={fmt.weekday(day)} testID={`${testID}-day-${day}`}>
                <Stack paddingX="3" paddingY="1" radius="full" border={on ? "accent.text" : "border.subtle"}>
                  <Text variant="footnote" weight="medium" color={on ? "text.link" : "text.secondary"}>
                    {fmt.weekday(day)}
                  </Text>
                </Stack>
              </Pressable>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}

function WebhookVariables({ trigger, onChange, testID }: { trigger: Extract<WorkflowTrigger, { type: "webhook" }>; onChange: (t: WorkflowTrigger) => void; testID: string }) {
  const t = useT();
  const vars = trigger.variables;
  const set = (variables: WebhookVariable[]) => onChange({ ...trigger, variables });
  return (
    <Stack gap="2" testID={testID}>
      <Text variant="footnote" weight="medium" color="text.secondary">
        {t("workflow.webhook.variables")}
      </Text>
      {vars.map((v, i) => (
        <Stack key={i} direction="horizontal" gap="2" align="start">
          <Stack grow>
            <TextField
              accessibilityLabel={t("workflow.webhook.key")}
              placeholder={t("workflow.webhook.key")}
              value={v.key}
              onChange={(key) => set(vars.map((x, j) => (j === i ? { ...x, key: key.toLowerCase().replace(/[^a-z0-9_]/g, "_") } : x)))}
              testID={`${testID}-key-${i}`}
            />
          </Stack>
          <Select accessibilityLabel={t("workflow.webhook.type")} value={v.type} onChange={(type) => set(vars.map((x, j) => (j === i ? { ...x, type } : x)))} options={WEBHOOK_TYPES.map((x) => ({ value: x, label: t(`workflow.varType.${x}`) }))} testID={`${testID}-type-${i}`} />
          <IconButton icon="trash" accessibilityLabel={t("workflow.webhook.remove")} onPress={() => set(vars.filter((_x, j) => j !== i))} testID={`${testID}-remove-${i}`} />
        </Stack>
      ))}
      <Stack direction="horizontal">
        <Button label={t("workflow.webhook.add")} icon="plus" variant="ghost" size="sm" onPress={() => set([...vars, { key: `var${vars.length + 1}`, type: "text" }])} disabled={vars.length >= 20} testID={`${testID}-add`} />
      </Stack>
    </Stack>
  );
}
